"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { BlogPost } from "@/types/blog";
import facebookIcon from "@/assets/fb.svg";
import twitterIcon from "@/assets/twitter.svg";
import pinterestIcon from "@/assets/pinterest.svg";
import linkedinIcon from "@/assets/linkedin.svg";
import ProcessSection from "@/components/ProcessSection";

interface BlogDetailClientProps {
  post: BlogPost;
  relatedPosts: BlogPost[];
  tableOfContents: { id: string; title: string; level: number }[];
}

export default function BlogDetailClient({
  post,
  relatedPosts,
  tableOfContents,
}: BlogDetailClientProps) {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const BlogCta = ({ position }: { position: "top" | "bottom" }) => {
    if (!post.cta) return null;

    return (
      <div
        className={`relative rounded-2xl border border-gray-200 border-t-4 border-t-[#7771B8]/70 bg-white p-6 sm:p-8 shadow-[0_14px_40px_rgba(17,24,39,0.08)] ${
          position === "bottom" ? "mt-8" : "mb-8"
        }`}
      >
        <div className="relative">
          <p className="inline-flex items-center rounded-full border border-[#7771B8]/25 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#5F579A]">
            Start Your Plan
          </p>

          <div className="mt-5 flex flex-col gap-6">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold leading-tight text-gray-900 font-[family-name:var(--font-playfair)]">
                Ready to take the next step?
              </h3>
              <p className="mt-2 text-sm sm:text-base text-gray-700 leading-relaxed">
                Get a clinically guided, personalised treatment pathway designed
                for sustainable results.
              </p>
            </div>

            <a
              href={post.cta.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex w-full items-center justify-between gap-4 self-start overflow-visible rounded-2xl bg-[#6E66B3] px-5 sm:px-6 py-4 text-left text-sm sm:text-base font-bold text-white shadow-[0_10px_24px_rgba(64,49,135,0.33)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#5F579A] hover:shadow-[0_14px_30px_rgba(64,49,135,0.42)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6E66B3]/60 focus-visible:ring-offset-2 sm:w-auto"
            >
              <span className="min-w-0 leading-snug break-words sm:whitespace-nowrap">
                {post.cta.label}
              </span>
              <span className="inline-flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-full bg-white/20 transition-transform duration-300 group-hover:translate-x-0.5">
                <svg
                  className="h-4 w-4 sm:h-5 sm:w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </span>
            </a>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Featured Image */}
      <section className="relative bg-gray-900 overflow-hidden">
        {post.featuredImage && (
          <>
            <div className="absolute inset-0">
              <Image
                src={post.featuredImage}
                alt={post.title}
                fill
                className="object-cover opacity-40"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent" />
          </>
        )}

        <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 2xl:px-8 py-16 sm:py-20 lg:py-24">
          {/* Breadcrumbs */}
          <nav
            className="flex items-center space-x-2 text-sm mb-6"
            aria-label="Breadcrumb"
          >
            <Link
              href="/"
              className="text-gray-300 hover:text-white transition-colors duration-200"
            >
              Home
            </Link>
            <span className="text-gray-500">/</span>
            <Link
              href="/blog"
              className="text-gray-300 hover:text-white transition-colors duration-200"
            >
              Blog
            </Link>
            <span className="text-gray-500">/</span>
            <span className="text-white font-medium">{post.category.name}</span>
          </nav>

          {/* Category Badge */}
          <div className="mb-4">
            <span className="inline-flex items-center px-4 py-2 bg-[#7771B8] rounded-lg text-xs font-bold text-white shadow-lg">
              {post.category.name}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight max-w-4xl font-[family-name:var(--font-playfair)]">
            {post.title}
          </h1>

          {/* Meta Information */}
          <div className="flex flex-wrap items-center gap-4 text-gray-300 text-sm">
            <div className="flex items-center gap-3">
              {post.author.avatar ? (
                <Image
                  src={post.author.avatar}
                  alt={post.author.name}
                  width={40}
                  height={40}
                  className="rounded-full border-2 border-white/20"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#7771B8] flex items-center justify-center text-white font-bold border-2 border-white/20">
                  {post.author.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <div className="text-white font-semibold">
                  {post.author.name}
                </div>
                {post.author.title && (
                  <div className="text-gray-400 text-xs">
                    {post.author.title}
                  </div>
                )}
              </div>
            </div>
            <span className="text-gray-500">•</span>
            <time dateTime={post.publishedAt.toISOString()}>
              {formatDate(post.publishedAt)}
            </time>
            {post.readTime && (
              <>
                <span className="text-gray-500">•</span>
                <span>{post.readTime} min read</span>
              </>
            )}
            {post.views && (
              <>
                <span className="text-gray-500">•</span>
                <span>{post.views.toLocaleString()} views</span>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 2xl:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:col-span-3 order-2 lg:order-1">
              <div className="lg:sticky lg:top-8">
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    Table of Contents
                  </h3>
                  <nav className="space-y-2">
                    {tableOfContents.map((item) => (
                      <a
                        key={item.id}
                        href={`#${item.id}`}
                        className={`block text-sm py-2 rounded-lg transition-colors duration-200 ${
                          item.level >= 3 ? "pl-6 pr-3" : "px-3"
                        } ${
                          activeSection === item.id
                            ? "bg-[#7771B8] text-white font-semibold"
                            : "text-gray-700 hover:bg-gray-200 hover:text-gray-900"
                        }`}
                        onClick={() => setActiveSection(item.id)}
                      >
                        {item.title}
                      </a>
                    ))}
                  </nav>

                  {/* Share Section */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h4 className="text-sm font-bold text-gray-900 mb-3">
                      Share this article
                    </h4>
                    <div className="flex gap-2">
                      {[
                        facebookIcon,
                        twitterIcon,
                        linkedinIcon,
                        pinterestIcon,
                      ].map((icon, idx) => (
                        <button
                          key={idx}
                          className="w-9 h-9 bg-white rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-100 hover:border-gray-300 transition-all duration-300 hover:scale-110 shadow-sm"
                        >
                          <Image
                            src={icon}
                            alt="Social"
                            width={14}
                            height={14}
                            className="opacity-70"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <article className="lg:col-span-9 order-1 lg:order-2">
              {/* Article Content */}
              <div className="bg-white rounded-xl p-6 sm:p-8 lg:p-10 border border-gray-200 shadow-sm">
                {/* Excerpt/Introduction */}
                <div className="mb-8 pb-8 border-b border-gray-200">
                  <p className="text-lg sm:text-xl text-gray-700 leading-relaxed font-medium">
                    {post.excerpt}
                  </p>
                </div>

                <BlogCta position="top" />

                {/* Main Content */}
                <div className="prose prose-lg max-w-none [&_h1]:font-bold [&_h1]:text-gray-900 [&_h1]:text-3xl [&_h1]:mb-4 [&_h1]:mt-8 [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:text-2xl sm:[&_h2]:text-3xl [&_h2]:mb-4 [&_h2]:mt-8 [&_h3]:font-bold [&_h3]:text-gray-900 [&_h3]:text-xl [&_h3]:mb-3 [&_h3]:mt-6 [&_h4]:font-bold [&_h4]:text-gray-900 [&_h4]:text-lg [&_h4]:mb-3 [&_h4]:mt-4 [&_p]:text-gray-900 [&_p]:leading-relaxed [&_p]:mb-4 [&_a]:text-[#7771B8] [&_a]:hover:underline [&_strong]:text-gray-900 [&_strong]:font-bold [&_em]:italic [&_li]:text-gray-900 [&_li]:mb-2 [&_ul]:text-gray-900 [&_ul]:mb-4 [&_ul]:ml-6 [&_ol]:text-gray-900 [&_ol]:mb-4 [&_ol]:ml-6 [&_blockquote]:border-l-4 [&_blockquote]:border-[#7771B8] [&_blockquote]:pl-4 [&_blockquote]:py-2 [&_blockquote]:italic [&_blockquote]:text-gray-700 [&_table]:w-full [&_table]:border-collapse [&_table]:mb-4 [&_thead]:bg-gray-100 [&_th]:border [&_th]:border-gray-300 [&_th]:px-3 [&_th]:py-2 [&_th]:font-bold [&_th]:text-gray-900 [&_th]:text-left [&_td]:border [&_td]:border-gray-300 [&_td]:px-3 [&_td]:py-2 [&_td]:text-gray-900">
                  <div dangerouslySetInnerHTML={{ __html: post.content }} />
                </div>

                <div className="pt-8 border-t border-gray-200">
                  <BlogCta position="bottom" />
                </div>

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-gray-200">
                    <h3 className="text-sm font-bold text-gray-900 mb-3">
                      Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-[#7771B8]/10 hover:text-[#7771B8] transition-colors duration-200 cursor-pointer"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Author Bio */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                    <div className="flex items-start gap-4">
                      {post.author.avatar ? (
                        <Image
                          src={post.author.avatar}
                          alt={post.author.name}
                          width={64}
                          height={64}
                          className="rounded-full"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-[#7771B8] flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                          {post.author.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                          {post.author.name}
                        </h3>
                        {post.author.title && (
                          <p className="text-sm text-[#7771B8] font-medium mb-2">
                            {post.author.title}
                          </p>
                        )}
                        {post.author.bio && (
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {post.author.bio}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <div className="mt-12">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 font-[family-name:var(--font-playfair)]">
                    Related Articles
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {relatedPosts.map((relatedPost) => (
                      <Link
                        key={relatedPost.id}
                        href={`/blog/${relatedPost.slug}`}
                        className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300"
                      >
                        <div className="relative aspect-[16/9] overflow-hidden">
                          {relatedPost.featuredImage ? (
                            <Image
                              src={relatedPost.featuredImage}
                              alt={relatedPost.title}
                              fill
                              className="object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-[#7771B8] to-[#6B5B9A]" />
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className="font-bold text-gray-900 line-clamp-2 group-hover:text-[#7771B8] transition-colors duration-200">
                            {relatedPost.title}
                          </h3>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </article>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <ProcessSection />
    </div>
  );
}
